/**
 * 存貨紀錄 Modal(新增 / 編輯 / 刪除 + 圖片)。
 * 新增 mode 圖片在 record 建立後再上傳(因為上傳流程需要 recordId)。
 */
import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type {
  CreateInventoryRecordInput,
  InventoryRecordResponse,
} from '@app/shared';
import { Modal } from '../../components/Modal';
import { InventoryForm } from './InventoryForm';
import {
  deleteInventoryImage,
  InventoryImageMimeError,
  uploadInventoryImage,
  useCreateInventoryRecord,
  useDeleteInventoryRecord,
  useInventoryImageUrl,
  useUpdateInventoryRecord,
} from './api';
import { getApiErrorMessage } from '../../lib/api-client';

/** MIME 檢查是前端自己丟的錯,訊息本身就是給使用者看的;其餘走 API 錯誤解析。 */
function uploadErrorMessage(err: unknown): string {
  if (err instanceof InventoryImageMimeError) return err.message;
  return getApiErrorMessage(err, '圖片上傳失敗');
}

export interface InventoryRecordModalProps {
  open: boolean;
  onClose: () => void;
  customerId: number;
  mode: 'new' | 'edit';
  record?: InventoryRecordResponse | null;
}

export function InventoryRecordModal(props: InventoryRecordModalProps): JSX.Element {
  const { open, onClose, customerId, mode, record } = props;
  const qc = useQueryClient();
  const createMutation = useCreateInventoryRecord(customerId);
  const updateMutation = useUpdateInventoryRecord(customerId);
  const deleteMutation = useDeleteInventoryRecord(customerId);

  /** 圖片上傳完成 / 刪除後重抓列表,讓縮圖即時出現/消失(不需 F5)。 */
  const invalidateList = (): void => {
    void qc.invalidateQueries({
      queryKey: ['inventory-records', customerId],
      exact: false,
    });
  };

  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [formDirty, setFormDirty] = useState(false);
  const [showUnsavedHint, setShowUnsavedHint] = useState(false);
  /** 本地追蹤「使用者已移除既有附件」;移除立即落 server,但 record prop 不會變,所以用此 flag 切畫面。 */
  const [attachmentRemoved, setAttachmentRemoved] = useState(false);

  const currentRecordId = mode === 'edit' ? record?.id : null;
  const imageUrlQuery = useInventoryImageUrl(
    currentRecordId,
    mode === 'edit' && !!record?.attachment,
  );

  const reset = (): void => {
    setPendingFile(null);
    setUploading(false);
    setUploadError(null);
    setFormDirty(false);
    setShowUnsavedHint(false);
    setAttachmentRemoved(false);
  };

  /** Header X / ESC / backdrop 觸發。編輯模式下若有未存變更,先顯示提示,第二次才真正關閉。 */
  const close = (): void => {
    if (uploading) return;
    const hasUnsavedChanges =
      mode === 'edit' && (formDirty || pendingFile !== null);
    if (hasUnsavedChanges && !showUnsavedHint) {
      setShowUnsavedHint(true);
      return;
    }
    reset();
    onClose();
  };

  /** 取消按鈕:明確使用者意圖,直接關閉不顯示提示。 */
  const cancelClose = (): void => {
    if (uploading) return;
    reset();
    onClose();
  };

  const handleCreate = async (values: CreateInventoryRecordInput): Promise<void> => {
    try {
      const created = await createMutation.mutateAsync(values);
      if (pendingFile) {
        setUploading(true);
        try {
          await uploadInventoryImage(created.id, pendingFile);
        } catch (err) {
          setUploadError(uploadErrorMessage(err));
          setUploading(false);
          return;
        }
        setUploading(false);
        // 圖片上傳完才 invalidate,縮圖可即時顯示
        invalidateList();
      }
      reset();
      onClose();
    } catch {
      /* 錯誤已由 mutation state 顯示 */
    }
  };

  const handleUpdate = async (values: CreateInventoryRecordInput): Promise<void> => {
    if (!record) return;
    try {
      await updateMutation.mutateAsync({ id: record.id, input: values });
      if (pendingFile) {
        setUploading(true);
        try {
          await uploadInventoryImage(record.id, pendingFile);
        } catch (err) {
          setUploadError(uploadErrorMessage(err));
          setUploading(false);
          return;
        }
        setUploading(false);
        invalidateList();
      }
      reset();
      onClose();
    } catch {
      /* mutation 狀態顯示錯誤 */
    }
  };

  const handleDeleteImage = async (): Promise<void> => {
    if (!record) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm('移除這張圖片?')) return;
    await deleteInventoryImage(record.id);
    invalidateList();
    setAttachmentRemoved(true);
  };

  const handleDelete = async (): Promise<void> => {
    if (!record) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`確定刪除「${record.itemName}」這筆存貨紀錄?`)) return;
    await deleteMutation.mutateAsync(record.id);
    reset();
    onClose();
  };

  const title = mode === 'new' ? '新增存貨紀錄' : '編輯存貨紀錄';

  return (
    <Modal
      open={open}
      onClose={close}
      title={title}
      dismissOnBackdrop={!uploading}
      widthClass="max-w-2xl"
      headerNote={
        showUnsavedHint ? (
          <span
            role="alert"
            className="text-sm text-amber-600 font-medium"
            data-testid="inv-unsaved-hint"
          >
            尚未儲存
          </span>
        ) : null
      }
    >
      <div className="grid gap-4">
        {createMutation.isError ? (
          <Banner>
            建立失敗:
            {getApiErrorMessage(createMutation.error, '未知錯誤')}
          </Banner>
        ) : null}
        {updateMutation.isError ? (
          <Banner>
            更新失敗:
            {getApiErrorMessage(updateMutation.error, '未知錯誤')}
          </Banner>
        ) : null}
        {uploadError ? <Banner>{uploadError}</Banner> : null}

        <InventoryForm
          {...(mode === 'edit' && record ? { defaultValues: record } : {})}
          submitting={
            createMutation.isPending || updateMutation.isPending || uploading
          }
          submitLabel={mode === 'new' ? '建立' : '更新'}
          onSubmit={mode === 'new' ? handleCreate : handleUpdate}
          onCancel={cancelClose}
          onDirtyChange={setFormDirty}
        />

        <div className="bg-slate-50 border rounded-md p-3 grid gap-2">
          <div className="text-sm font-medium text-slate-700">圖片(選填,單張)</div>

          {mode === 'edit' && record?.attachment && !attachmentRemoved ? (
            <div className="flex items-center gap-3">
              {imageUrlQuery.data ? (
                <img
                  src={imageUrlQuery.data}
                  alt={record.attachment.originalName}
                  className="max-h-32 rounded border"
                />
              ) : (
                <div className="text-xs text-slate-500">載入縮圖中…</div>
              )}
              <div className="grid gap-1">
                <div className="text-xs text-slate-700 break-all">
                  {record.attachment.originalName}
                </div>
                <button
                  type="button"
                  onClick={handleDeleteImage}
                  className="text-xs text-red-600 border border-red-300 rounded px-2 py-0.5 hover:bg-red-50 w-fit"
                  data-testid="inv-image-delete"
                >
                  移除圖片
                </button>
              </div>
            </div>
          ) : null}

          <InventoryImagePicker
            file={pendingFile}
            onChange={setPendingFile}
            disabled={uploading}
            replace={mode === 'edit' && !!record?.attachment && !attachmentRemoved}
          />
          {uploading ? (
            <div className="text-xs text-slate-500">圖片上傳中…</div>
          ) : null}
        </div>

        {mode === 'edit' && record ? (
          <div className="border-t pt-3 flex justify-end">
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              className="text-sm border rounded px-3 py-1.5 text-red-600 border-red-300 hover:bg-red-50 disabled:opacity-50"
              data-testid="btn-delete-inventory"
            >
              {deleteMutation.isPending ? '刪除中…' : '刪除這筆紀錄'}
            </button>
          </div>
        ) : null}
      </div>
    </Modal>
  );
}

function Banner({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div role="alert" className="bg-red-50 text-red-700 text-sm p-2 rounded">
      {children}
    </div>
  );
}

const IMAGE_MAX_BYTES = 10 * 1024 * 1024;
const IMAGE_ACCEPT = 'image/jpeg,image/png,image/webp';
const IMAGE_EXT_RE = /\.(jpe?g|png|webp)$/i;

function InventoryImagePicker({
  file,
  onChange,
  disabled,
  replace,
}: {
  file: File | null;
  onChange: (f: File | null) => void;
  disabled: boolean;
  replace: boolean;
}): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validate = (f: File): string | null => {
    if (f.size > IMAGE_MAX_BYTES) return '檔案超過 10 MB';
    if (!IMAGE_EXT_RE.test(f.name)) return '僅支援 JPEG / PNG / WebP';
    return null;
  };

  const accept = (f: File | undefined): void => {
    if (!f) return;
    const err = validate(f);
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    onChange(f);
  };

  const prompt = file
    ? null
    : replace
      ? '拖放新圖或點擊選檔(會覆蓋既有圖片;JPEG / PNG / WebP,10 MB 內)'
      : '拖放圖片到此或點擊選檔(JPEG / PNG / WebP,10 MB 內)';

  return (
    <div className="grid gap-2">
      <div
        role="button"
        tabIndex={0}
        aria-label="圖片選擇區"
        onClick={() => !disabled && inputRef.current?.click()}
        onKeyDown={(e) => {
          if (!disabled && (e.key === 'Enter' || e.key === ' ')) inputRef.current?.click();
        }}
        onDragOver={(e) => {
          if (disabled) return;
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          if (disabled) return;
          e.preventDefault();
          setDragOver(false);
          accept(e.dataTransfer.files?.[0]);
        }}
        className={`text-sm text-center px-3 py-2 border-2 border-dashed rounded ${
          disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
        } ${
          dragOver
            ? 'border-blue-500 bg-blue-50'
            : 'border-slate-300 bg-slate-50 hover:bg-slate-100'
        }`}
      >
        {prompt ?? (
          <div className="flex items-center justify-center gap-2 text-slate-700">
            <span className="truncate max-w-[18rem]">{file!.name}</span>
            <span className="text-xs text-slate-400">
              ({Math.round(file!.size / 1024)} KB)
            </span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onChange(null);
                setError(null);
              }}
              className="text-xs text-red-600 hover:underline"
              aria-label="移除選擇的圖片"
            >
              移除
            </button>
          </div>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={IMAGE_ACCEPT}
        aria-label="圖片檔案選擇"
        className="hidden"
        onChange={(e) => {
          accept(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
      {error ? (
        <div role="alert" className="text-xs text-red-600">
          {error}
        </div>
      ) : null}
    </div>
  );
}
